import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentRef, inject, Injectable, Injector, OnDestroy, signal } from '@angular/core';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { CeSnackbarComponent } from './ce-snackbar.component';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { CE_SNACKBAR_CLOSE, CE_SNACKBAR_DATA, CeSnackbarDelays, CeSnackbarState, SnackbarData } from './ce-snackbar.tokens';

export {
    CE_SNACKBAR_CLOSE,
    CE_SNACKBAR_DATA,
    CeSnackbarDelays,
    CeSnackbarState,
} from './ce-snackbar.tokens';
export type { SnackbarAction, SnackbarData } from './ce-snackbar.tokens';

@Injectable({
    providedIn: 'root',
})
export class CeSnackbarService implements OnDestroy {
    #lastOverlayRef: OverlayRef | null = null;
    #timeoutId: ReturnType<typeof setTimeout> | null = null;
    #animationTimeoutId: ReturnType<typeof setTimeout> | null = null;
    #snackbarComponentRef: ComponentRef<CeSnackbarComponent> | null = null;
    #routeChangeSubscription: Subscription;
    #overlayContainerService = inject(CeOverlayContainerService);
    #snackbarOverlayContainer!: HTMLDivElement | null;

    snackbarData = signal<SnackbarData | null>(null);
    // TODO: Add a method for after the snackbar is closed

    constructor(private overlay: Overlay, private injector: Injector, private router: Router) {
        this.#routeChangeSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                if (this.snackbarData()?.closeOnNavigation) {
                    this.closeSnackbar();
                }
            }
        });
    }

    showLoadingSnackbar(message: string) {
        this.showSnackbar({
            message,
            state: CeSnackbarState.LOADING,
            duration: 'persist',
            showCloseButton: false,
        });
        return {
            close: () => this.closeSnackbar(),
        };
    }

    ngOnDestroy(): void {
        if (this.snackbarData()?.closeOnNavigation) {
            this.#timeoutId && clearTimeout(this.#timeoutId);
            this.#animationTimeoutId && clearTimeout(this.#animationTimeoutId);
        }
        this.#routeChangeSubscription.unsubscribe();
    }

    showSnackbar(data: SnackbarData) {
        const defaultConfig: SnackbarData = {
            message: data.message,
            closeOnNavigation: true,
        };
        // Keep the default configuration and override only the values that are passed with the data
        const snackbarConfig = { ...defaultConfig, ...data };

        this.snackbarData.set(snackbarConfig);
        // If there is an existing snackbar, close it
        if (this.#lastOverlayRef) {
            this.#lastOverlayRef?.detach();
            this.#lastOverlayRef = null;
            this.#timeoutId && clearTimeout(this.#timeoutId);
        }
        // Cancel any pending detach from a previous closeSnackbar() animation; otherwise it would
        // detach the new overlay we're about to create ~300ms later.
        if (this.#animationTimeoutId) {
            clearTimeout(this.#animationTimeoutId);
            this.#animationTimeoutId = null;
        }
        if (this.#snackbarOverlayContainer) {
            this.#overlayContainerService.removeOverlayContainer(this.#snackbarOverlayContainer);
            this.#snackbarOverlayContainer = null;
        }

        const positionStrategy = this.overlay.position().global().centerHorizontally();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            positionStrategy,
        });

        this.#lastOverlayRef = this.overlay.create(overlayConfig);

        const injectionTokens = new WeakMap();
        injectionTokens.set(CE_SNACKBAR_DATA, this.snackbarData());
        injectionTokens.set(CE_SNACKBAR_CLOSE, () => this.closeSnackbar());
        const injector = new PortalInjector(this.injector, injectionTokens);

        const portal = new ComponentPortal(CeSnackbarComponent, null, injector);
        const snackbarComponentRef = this.#lastOverlayRef.attach(portal);
        this.#snackbarComponentRef = snackbarComponentRef;

        this.#snackbarOverlayContainer = this.#overlayContainerService.createOverlayContainer('snackbar-overlay-container');

        // If a custom overlay target is provided (e.g. dialog's overlay container), re-parent the
        // snackbar overlay container into it so the snackbar appears above that stacking context.
        const customOverlay = snackbarConfig.customOverlay;
        if (customOverlay?.enabled) {
            const target = document.querySelector(customOverlay.overlaySelector);
            if (target) {
                target.appendChild(this.#snackbarOverlayContainer);
            }
        }

        this.#snackbarOverlayContainer.appendChild(this.#lastOverlayRef.hostElement);

        // Auto-dismiss logic
        const current = this.snackbarData();
        if (!current) {
            return;
        }

        // Error snackbars: persist by default (no auto-dismiss) unless explicit numeric duration is set
        if (current.state === CeSnackbarState.ERROR) {
            if (typeof current.duration === 'number') {
                this.#timeoutId = setTimeout(() => {
                    this.closeSnackbar();
                }, current.duration);
            }
            return;
        }

        // Non-error snackbars
        if (current.duration === 'persist') {
            return;
        }

        const hasActions = Boolean(current.actions?.length);
        const hasExplicitClose = current.showCloseButton === true;

        let finalDuration: number;
        if (typeof current.duration === 'number') {
            finalDuration = current.duration;
        } else if (hasActions || hasExplicitClose) {
            finalDuration = CeSnackbarDelays.ACTIONS_VISIBILITY;
        } else {
            finalDuration = CeSnackbarDelays.VISIBILITY;
        }

        this.#timeoutId = setTimeout(() => {
            this.closeSnackbar();
        }, finalDuration);
    }

    closeSnackbar() {
        if (this.#lastOverlayRef) {
            this.#timeoutId && clearTimeout(this.#timeoutId);
            this.#snackbarComponentRef?.instance.close();

            //Wait for the animation to finish before detaching the snackbar
            this.#animationTimeoutId = setTimeout(() => {
                this.#lastOverlayRef?.detach();
                this.#lastOverlayRef = null;
                this.#snackbarComponentRef = null;
                this.#overlayContainerService.removeOverlayContainer(this.#snackbarOverlayContainer);
            }, CeSnackbarDelays.ANIMATION);
        }
    }
}
